import React, { useEffect, useMemo, useState } from 'react';
import { Search, ChevronDown, ChevronRight, Check, X, Settings, RotateCcw } from 'lucide-react';
import { useTranslation } from '../../../contexts/I18nProvider';
import { settingsService } from '../../../services/settingsService';
import { SettingWithValue, SettingCategory, DATA_TYPE_DISPLAY_NAMES } from '../../../types/settings';
import { parseDefaultReportIdType } from '../../../utils/reportSettings';

/** Minimal rapportmal for «DefaultReportId:{type}»-nedtrekket. */
export interface AdvancedSettingReportOption {
    reportID: number;
    reportName?: string | null;
    description?: string | null;
}

interface AdvancedSettingsPanelProps {
    /**
     * 'system' — redigerer SystemSetting (verdi = systemValue, tilbakestill når systemverdi finnes).
     * 'profile' — redigerer ProfileSetting for én bruker (verdi = profileValue, tilbakestill når profilen overstyrer).
     */
    mode: 'system' | 'profile';
    settings: SettingWithValue[];
    categories: SettingCategory[];
    /** Aktive rapportmaler per rapporttype (for DefaultReportId-settinger). */
    reportsByType?: Record<number, AdvancedSettingReportOption[]>;
    /** Nøkkelen som lagres/tilbakestilles nå (viser spinner og sperrer knapper). */
    savingKey?: string | null;
    /** Lagrer verdien. Returnerer false hvis lagringen ble avvist (editoren holdes da åpen). */
    onSave: (settingKey: string, value: string) => Promise<boolean | void>;
    onReset: (settingKey: string) => Promise<void>;
    /** Ekstern søketekst. Utelates den, viser panelet sitt eget søkefelt. */
    searchTerm?: string;
}

/**
 * Felles visning av «Avanserte innstillinger» (SettingDefinition-baserte) for
 * Systeminnstillinger og Profiladministrasjon: søk, sammenleggbare kategorier og
 * én tabellrad per innstilling med rediger/lagre/tilbakestill.
 */
export const AdvancedSettingsPanel: React.FC<AdvancedSettingsPanelProps> = ({
    mode,
    settings,
    categories,
    reportsByType = {},
    savingKey = null,
    onSave,
    onReset,
    searchTerm: externalSearchTerm
}) => {
    const { t } = useTranslation();
    const [internalSearchTerm, setInternalSearchTerm] = useState('');
    const searchTerm = externalSearchTerm ?? internalSearchTerm;
    const [expandedCategories, setExpandedCategories] = useState<Set<string> | null>(null);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');

    const groupedSettings = useMemo(
        () => settingsService.groupByCategory(settings, categories),
        [settings, categories]
    );

    // Første kategori åpen ved første lasting; brukerens valg bevares ved reload
    useEffect(() => {
        if (expandedCategories === null && groupedSettings.length > 0) {
            setExpandedCategories(new Set([groupedSettings[0].category]));
        }
    }, [groupedSettings, expandedCategories]);

    const filteredGroups = useMemo(() => {
        if (!searchTerm.trim()) return groupedSettings;
        const lowerSearch = searchTerm.toLowerCase();
        return groupedSettings
            .map(group => ({
                ...group,
                settings: group.settings.filter(s =>
                    s.definition.settingKey.toLowerCase().includes(lowerSearch) ||
                    s.definition.displayName.toLowerCase().includes(lowerSearch) ||
                    s.definition.description?.toLowerCase().includes(lowerSearch)
                )
            }))
            .filter(group => group.settings.length > 0);
    }, [groupedSettings, searchTerm]);

    // Ved søk vises alle treff utfoldet
    const isExpanded = (category: string) =>
        searchTerm.trim() !== '' || (expandedCategories?.has(category) ?? false);

    const toggleCategory = (category: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev ?? []);
            if (next.has(category)) {
                next.delete(category);
            } else {
                next.add(category);
            }
            return next;
        });
    };

    const ownValue = (setting: SettingWithValue) =>
        mode === 'system' ? setting.systemValue : setting.profileValue;

    const hasOwnValue = (setting: SettingWithValue) =>
        mode === 'system'
            ? setting.systemValue !== undefined && setting.systemValue !== null
            : setting.effectiveSource === 'Profile';

    const startEditing = (setting: SettingWithValue) => {
        setEditingKey(setting.definition.settingKey);
        setEditValue(ownValue(setting) ?? setting.currentValue ?? '');
    };

    const cancelEditing = () => {
        setEditingKey(null);
        setEditValue('');
    };

    const save = async (settingKey: string) => {
        try {
            const result = await onSave(settingKey, editValue);
            if (result !== false) cancelEditing();
        } catch {
            // Feilmelding vises av forelderen; editoren holdes åpen
        }
    };

    const reset = async (settingKey: string) => {
        if (!window.confirm(t('manager.modules.adminHost.systemSettings.resetConfirm', {}, 'Er du sikker på at du vil tilbakestille denne innstillingen til standard?'))) {
            return;
        }
        await onReset(settingKey);
    };

    const sourceLabel = (source: SettingWithValue['effectiveSource']) =>
        source === 'System' ? t('manager.modules.adminHost.systemSettings.sourceSystem', {}, 'System')
            : source === 'Profile' ? t('manager.modules.adminHost.systemSettings.sourceProfile', {}, 'Profil')
                : t('manager.modules.adminHost.systemSettings.sourceDefault', {}, 'Standard');

    const sourceClass = (source: SettingWithValue['effectiveSource']) =>
        source === 'System' ? 'text-blue-600' : source === 'Profile' ? 'text-purple-600' : 'text-gray-400';

    const renderValueEditor = (setting: SettingWithValue) => {
        const baseClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

        // «DefaultReportId:{type}» — nedtrekk med aktive rapportmaler av typen.
        // Tom verdi (eller 0) = «Auto/innebygd».
        const reportType = parseDefaultReportIdType(setting.definition.settingKey);
        if (reportType !== null) {
            const reports = reportsByType[reportType] ?? [];
            return (
                <select value={editValue === '0' ? '' : editValue} onChange={(e) => setEditValue(e.target.value)} className={baseClass}>
                    <option value="">{t('manager.modules.adminHost.systemSettings.defaultReportAuto', {}, 'Auto / innebygd standard')}</option>
                    {reports.map((r) => (
                        <option key={r.reportID} value={String(r.reportID)}>
                            {(r.description || r.reportName || `Report ${r.reportID}`)} (ID {r.reportID})
                        </option>
                    ))}
                </select>
            );
        }

        switch (setting.definition.dataType.toLowerCase()) {
            case 'bool':
                return (
                    <select value={editValue} onChange={(e) => setEditValue(e.target.value)} className={baseClass}>
                        <option value="">{t('manager.modules.adminHost.systemSettings.selectPlaceholder', {}, '-- Velg --')}</option>
                        <option value="true">{t('manager.modules.adminHost.systemSettings.yes', {}, 'Ja')}</option>
                        <option value="false">{t('manager.modules.adminHost.systemSettings.no', {}, 'Nei')}</option>
                    </select>
                );
            case 'int':
                return <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} className={baseClass} />;
            case 'decimal':
                return <input type="number" step="0.01" value={editValue} onChange={(e) => setEditValue(e.target.value)} className={baseClass} />;
            case 'json':
                return <textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} rows={4} className={baseClass + ' font-mono text-sm'} placeholder='{"key": "value"}' />;
            default:
                return <input type="text" value={editValue} onChange={(e) => setEditValue(e.target.value)} className={baseClass} />;
        }
    };

    const renderRow = (setting: SettingWithValue) => {
        const { settingKey, dataType } = setting.definition;
        const isEditing = editingKey === settingKey;
        const isSaving = savingKey === settingKey;

        return (
            <tr key={settingKey} className="hover:bg-gray-50">
                <td className="px-4 py-2">
                    <div className="font-medium text-gray-900 text-sm">{setting.definition.displayName}</div>
                    {setting.definition.description && <div className="text-xs text-gray-500">{setting.definition.description}</div>}
                    <div className="text-xs text-gray-400 font-mono">{settingKey}</div>
                </td>
                <td className="px-4 py-2 text-center">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {DATA_TYPE_DISPLAY_NAMES[dataType] || dataType}
                    </span>
                </td>
                <td className="px-4 py-2">
                    {isEditing ? (
                        <div className="space-y-2">
                            {renderValueEditor(setting)}
                            <div className="flex gap-2">
                                <button onClick={() => save(settingKey)} disabled={isSaving}
                                    className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-sm">
                                    {isSaving ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Check className="w-4 h-4" />}
                                    {t('common.buttons.save', {}, 'Lagre')}
                                </button>
                                <button onClick={cancelEditing} disabled={isSaving}
                                    className="flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 text-sm">
                                    <X className="w-4 h-4" /> {t('common.buttons.cancel', {}, 'Avbryt')}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between">
                            <div>
                                <span className="text-gray-900 text-sm">{settingsService.formatValue(setting.currentValue, dataType)}</span>
                                <span className={`ml-2 text-xs ${sourceClass(setting.effectiveSource)}`}>
                                    ({sourceLabel(setting.effectiveSource)})
                                </span>
                                {mode === 'profile' && setting.effectiveSource === 'Profile' && setting.systemValue && (
                                    <div className="text-xs text-gray-400">
                                        {t('manager.modules.adminHost.systemSettings.sourceSystem', {}, 'System')}: {settingsService.formatValue(setting.systemValue, dataType)}
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => startEditing(setting)} className="text-blue-600 hover:text-blue-800 p-1" title={t('common.buttons.edit', {}, 'Rediger')}>
                                    <Settings className="w-4 h-4" />
                                </button>
                                {hasOwnValue(setting) && (
                                    <button onClick={() => reset(settingKey)} className="text-orange-600 hover:text-orange-800 p-1" title={t('manager.modules.adminHost.systemSettings.resetToDefault', {}, 'Tilbakestill til standard')} disabled={isSaving}>
                                        {isSaving ? <span className="block animate-spin h-4 w-4 border-2 border-orange-600 border-t-transparent rounded-full" /> : <RotateCcw className="w-4 h-4" />}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </td>
                <td className="px-4 py-2 text-gray-500 text-sm">
                    {setting.definition.defaultValue !== undefined && setting.definition.defaultValue !== null && setting.definition.defaultValue !== ''
                        ? settingsService.formatValue(setting.definition.defaultValue, dataType)
                        : '-'}
                </td>
            </tr>
        );
    };

    return (
        <div className="space-y-4">
            {externalSearchTerm === undefined && (
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder={t('manager.modules.adminHost.systemSettings.searchPlaceholder', {}, 'Søk i innstillinger...')}
                        value={internalSearchTerm}
                        onChange={(e) => setInternalSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            )}

            {filteredGroups.length === 0 ? (
                <div className="bg-white rounded-lg p-8 text-center text-gray-500 shadow-sm border border-gray-200">
                    {searchTerm ? t('manager.modules.adminHost.systemSettings.noSettingsForSearch', {}, 'Ingen innstillinger funnet for søket') : t('manager.modules.adminHost.systemSettings.noAdvancedSettings', {}, 'Ingen avanserte innstillinger tilgjengelig')}
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredGroups.map(group => (
                        <div key={group.category} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            <button onClick={() => toggleCategory(group.category)}
                                className="w-full flex items-center justify-between px-6 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                                <div className="flex items-center gap-3">
                                    {isExpanded(group.category) ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
                                    <span className="font-semibold text-gray-900">{group.displayName}</span>
                                    <span className="text-sm text-gray-500">{t('manager.modules.adminHost.systemSettings.settingsCount', { '0': group.settings.length }, `(${group.settings.length} innstillinger)`)}</span>
                                </div>
                            </button>
                            {isExpanded(group.category) && (
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-t border-gray-200">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">{t('manager.modules.adminHost.systemSettings.colSetting', {}, 'Innstilling')}</th>
                                            <th className="px-4 py-2 text-center text-sm font-medium text-gray-600 w-24">{t('common.fields.type', {}, 'Type')}</th>
                                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">{t('manager.modules.adminHost.systemSettings.colValue', {}, 'Verdi')}</th>
                                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 w-32">{t('manager.modules.adminHost.systemSettings.colDefault', {}, 'Standard')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {group.settings.map(setting => renderRow(setting))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Verdikilde */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-2">{t('manager.modules.adminHost.systemSettings.valueSourceTitle', {}, 'Verdikilde:')}</h3>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-blue-600"></span><span className="text-gray-600">{t('manager.modules.adminHost.systemSettings.legendSystem', {}, 'System - Satt for denne tenanten')}</span></div>
                    <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-purple-600"></span><span className="text-gray-600">{t('manager.modules.adminHost.systemSettings.legendProfile', {}, 'Profil - Satt per bruker')}</span></div>
                    <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-gray-400"></span><span className="text-gray-600">{t('manager.modules.adminHost.systemSettings.legendDefault', {}, 'Standard - Bruker standardverdi')}</span></div>
                </div>
            </div>
        </div>
    );
};

export default AdvancedSettingsPanel;
